import { useEffect, useState, type RefObject } from 'react';

import type { PearlLight } from '~/shared/ui/PearlMark';

import {
  HERO_LIGHT_AT_REST,
  HERO_LIGHT_FIRST_PULSE_MS,
  HERO_LIGHT_PULSE_MS,
  HERO_LIGHT_REST_MS,
  HERO_LIGHT_REST_SPREAD_MS,
  HERO_LIGHT_SETTLE_MS,
  heroLight,
  type HeroLightEvent,
} from '../model/heroLight';

/**
 * The hero figure's light, driven by the composer's field (board P5e, variant D): its focus, its
 * blur and every input, where the caret stands, the second after the last input, and the waiting
 * pulse every 6 to 8 s. Reads the field's own events rather than asking the composer for them, so
 * the composer's interface does not grow for a picture beside it.
 *
 * Off (`enabled` false, or the screen asks for reduced motion), the figure stands still and dark:
 * no timer runs and no event is listened to.
 */
export function useHeroLight(field: RefObject<HTMLTextAreaElement | null>, enabled: boolean, reducedMotion: boolean): PearlLight {
  const [light, setLight] = useState(HERO_LIGHT_AT_REST);
  const live = enabled && !reducedMotion;

  useEffect(() => {
    const node = field.current;
    if (!live || node === null) return undefined;
    const send = (event: HeroLightEvent) => {
      setLight((current) => heroLight(current, event));
    };
    // The field may already hold focus (a conversation started with „Nowa rozmowa").
    if (document.activeElement === node) send({ kind: 'focus' });

    let settle = 0;
    const onFocus = () => {
      send({ kind: 'focus' });
    };
    const onBlur = () => {
      window.clearTimeout(settle);
      send({ kind: 'blur' });
    };
    // Heard on the document, after React's own root listener has read the new value: a render
    // flushed between the field's listeners and React's would write the old value back over it.
    const onInput = (event: Event) => {
      if (event.target !== node) return;
      send({ kind: 'input', at: caretAt(node) });
      window.clearTimeout(settle);
      settle = window.setTimeout(() => {
        send({ kind: 'settle' });
      }, HERO_LIGHT_SETTLE_MS);
    };
    node.addEventListener('focus', onFocus);
    node.addEventListener('blur', onBlur);
    document.addEventListener('input', onInput);

    // The waiting pulse: one now and then, at a random rest so it never reads as a metronome. A
    // pulse due while the trainer is in the composer is skipped by the model, and the clock runs on.
    let pulse = 0;
    const nextPulse = (after: number) => {
      pulse = window.setTimeout(() => {
        send({ kind: 'pulse' });
        nextPulse(HERO_LIGHT_PULSE_MS + HERO_LIGHT_REST_MS + Math.random() * HERO_LIGHT_REST_SPREAD_MS);
      }, after);
    };
    nextPulse(HERO_LIGHT_FIRST_PULSE_MS);

    return () => {
      window.clearTimeout(settle);
      window.clearTimeout(pulse);
      node.removeEventListener('focus', onFocus);
      node.removeEventListener('blur', onBlur);
      document.removeEventListener('input', onInput);
    };
  }, [field, live]);

  return live ? light : HERO_LIGHT_AT_REST;
}

let measure: CanvasRenderingContext2D | null = null;

/** Where the caret stands across the field's width, 0 to 1, on the line it is on. */
function caretAt(node: HTMLTextAreaElement): number {
  measure ??= document.createElement('canvas').getContext('2d');
  if (measure === null) return 0;
  const style = getComputedStyle(node);
  measure.font = `${style.fontWeight} ${style.fontSize} ${style.fontFamily}`;
  const before = node.value.slice(0, node.selectionStart);
  const line = before.slice(before.lastIndexOf('\n') + 1);
  const width = node.clientWidth - (parseFloat(style.paddingLeft) || 0) - (parseFloat(style.paddingRight) || 0);
  return width <= 0 ? 0 : measure.measureText(line).width / width;
}
